import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from '@core/service/auth.service';
import { RestClientService } from '@core/service/rest-client.service';
import { UtilitiesService } from '@core/service/utilities.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-utilisateurs',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule],
  templateUrl: './utilisateurs.component.html',
  styleUrl: './utilisateurs.component.scss',
})
export class UtilisateursComponent implements OnInit {
  user: any;
  items: any[] = [];
  civilites: any[] = [];
  genres: any[] = [];
  roles: any[] = [];
  typesUtilisateur: any[] = [];
  activites: any[] = [];
  autresActivites: any[] = [];
  itemToSave: any = {};
  searchKey = '';
  loading = false;
  saving = false;
  showModal = false;
  currentPage = 0;
  itemsPerPage = 10;
  totalItems = 0;
  readonly maxBirthDate = (() => {
    const value = new Date();
    value.setFullYear(value.getFullYear() - 18);
    return value.toISOString().slice(0, 10);
  })();

  constructor(
    private authService: AuthService,
    private restClient: RestClientService,
    private utilities: UtilitiesService,
  ) {
    this.user = this.authService.getCurrentUser();
  }

  ngOnInit(): void {
    this.loadLookups();
    this.getItems();
  }

  private request(data: any = {}) {
    return {
      user_id: this.user.id,
      filiale_id: this.user.filiale_id,
      index: 0,
      size: 1000,
      data,
    };
  }

  private loadLookups(): void {
    this.restClient.post('civilite/getByCriteria', this.request()).subscribe((res: any) => this.civilites = res?.items ?? []);
    this.restClient.post('genre/getByCriteria', this.request()).subscribe((res: any) => this.genres = res?.items ?? []);
    this.restClient.post('role/getByCriteria', this.request()).subscribe((res: any) => this.roles = res?.items ?? []);
    this.restClient.post('typeUser/getByCriteria', this.request()).subscribe((res: any) => this.typesUtilisateur = res?.items ?? []);
    this.restClient.post('activiteAutre/getByCriteria', this.request()).subscribe((res: any) => this.autresActivites = res?.items ?? []);
    if (this.user?.prestataire_id) {
      this.restClient.post('activitePrestataire/getByCriteria', this.request({ prestataire_id: this.user.prestataire_id }))
        .subscribe((res: any) => this.activites = res?.items ?? []);
    }
  }

  getItems(): void {
    this.loading = true;
    const request: any = {
      ...this.request({ prestataire_id: this.user.prestataire_id }),
      index: this.currentPage,
      size: this.itemsPerPage,
    };
    if (this.searchKey.trim()) request.search_key = this.searchKey.trim();
    this.restClient.post('user/getByCriteria', request).subscribe({
      next: (res: any) => {
        this.items = res?.items ?? [];
        this.totalItems = res?.count ?? res?.totalItems ?? this.items.length;
        this.loading = false;
      },
      error: () => { this.items = []; this.loading = false; },
    });
  }

  openCreate(): void {
    this.itemToSave = {
      prestataire_id: this.user.prestataire_id,
      date_effet: new Date().toISOString().slice(0, 10),
      date_fin: '2030-12-31',
    };
    this.resetChecks();
    this.showModal = true;
  }

  openEdit(item: any): void {
    this.itemToSave = {
      ...item,
      civilite_id: item.civilite_id ?? item.civilite?.id ?? item.civilite,
      genre_id: item.genre_id ?? item.genre?.id ?? item.genre,
      date_naissance: item.date_naissance?.slice(0, 10),
      validity: item.validity?.slice(0, 10),
    };
    this.resetChecks();
    this.loadUserActivities(item.id);
    this.showModal = true;
  }

  closeModal(): void {
    if (!this.saving) this.showModal = false;
  }

  private resetChecks(): void {
    this.activites.forEach(item => item.is_checked = false);
    this.autresActivites.forEach(item => item.is_checked = false);
  }

  private loadUserActivities(userId: number): void {
    this.restClient.post('activiteUtilisateur/getByCriteria', this.request({
      prestataire_id: this.user.prestataire_id,
      user_id: userId,
    })).subscribe((res: any) => {
      const ids = new Set((res?.items ?? []).map((item: any) => item.activite_id));
      this.activites.forEach(item => item.is_checked = ids.has(item.activite_id));
    });
    this.restClient.post('activiteAutreUser/getByCriteria', this.request({ user_id: userId }))
      .subscribe((res: any) => {
        const ids = new Set((res?.items ?? []).map((item: any) => item.activite_autre_id));
        this.autresActivites.forEach(item => item.is_checked = ids.has(item.id));
      });
  }

  private isAdult(value: string): boolean {
    if (!value) return false;
    const birth = new Date(`${value}T00:00:00`);
    const limit = new Date();
    limit.setFullYear(limit.getFullYear() - 18);
    return !Number.isNaN(birth.getTime()) && birth <= limit;
  }

  save(): void {
    const item = this.itemToSave;
    const roleId = this.roles.find(role => role.codification === 'ROLE-PRESTAT')?.id;
    const typeUserId = this.typesUtilisateur.find(type => type.codification === 'USER_PRESTATAIRE')?.id;
    if (!item.nom || !item.prenom || !item.login || !item.email || !item.telephone || !item.date_naissance ||
        !item.civilite_id || !item.genre_id || !item.validity) {
      this.utilities.openSwalError('Veuillez renseigner tous les champs obligatoires.');
      return;
    }
    if (!this.isAdult(item.date_naissance)) {
      this.utilities.openSwalError("L'utilisateur doit être âgé d'au moins 18 ans.");
      return;
    }
    if (!item.id && !item.password) {
      this.utilities.openSwalError('Veuillez renseigner le mot de passe.');
      return;
    }
    if (!roleId || !typeUserId) {
      this.utilities.openSwalError("Le rôle ou le type d'utilisateur prestataire est introuvable.");
      return;
    }

    this.saving = true;
    const payload = {
      ...item,
      prestataire_id: this.user.prestataire_id,
      filiale_id: this.user.filiale_id,
      role_id: roleId,
      type_user_id: typeUserId,
      itemsActivites: this.activites.filter(a => a.is_checked).map(a => ({
        id: a.activite_id,
        date_effet: a.date_effet,
        date_fin: a.date_fin,
      })),
      itemsActivitesAutre: this.autresActivites.filter(a => a.is_checked).map(a => ({
        id: a.id,
        date_effet: a.date_effet,
        date_fin: a.date_fin,
      })),
    };
    this.restClient.post(`user/${item.id ? 'update' : 'create'}`, {
      user_id: this.user.id,
      filiale_id: this.user.filiale_id,
      datas: [payload],
    }).subscribe({
      next: (res: any) => {
        this.saving = false;
        if (res?.hasError) {
          this.utilities.openSwalError(res?.status?.message ?? "Échec de l'enregistrement.");
          return;
        }
        this.showModal = false;
        this.utilities.openSwalSuccess(res?.status?.message ?? 'Utilisateur enregistré.');
        this.getItems();
      },
      error: () => { this.saving = false; this.utilities.openSwalError('Service indisponible.'); },
    });
  }

  delete(item: any): void {
    Swal.fire({ title: 'Supprimer cet utilisateur ?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Oui', cancelButtonText: 'Non' })
      .then(result => {
        if (!result.isConfirmed) return;
        this.restClient.post('user/delete', { ...this.request(), datas: [{ id: item.id }] })
          .subscribe(() => this.getItems());
      });
  }

  resetPassword(item: any): void {
    Swal.fire({ title: 'Réinitialiser le mot de passe ?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Oui', cancelButtonText: 'Non' })
      .then(result => {
        if (!result.isConfirmed) return;
        this.restClient.post('user/resetAccountWithLogin', {
          ...this.request(),
          data: item,
        }).subscribe((res: any) => res?.hasError
          ? this.utilities.openSwalError(res?.status?.message)
          : this.utilities.openSwalSuccess(res?.status?.message));
      });
  }
}
